"use client";

import { useCallback, useDeferredValue, useMemo, useState } from "react";
import { Button, HStack, Text, VStack } from "@chakra-ui/react";
import type { MindstringPrototype, Situation } from "@/generated/fastapi";
import {
  countSituationsByPrototype,
  useAssignableSituations,
  useSituationsQuery,
} from "@/features/situations";
import { MEILI_SEARCH_CONFIG } from "@/features/search";
import { DEFAULT_HITS_PER_PAGE } from "@/features/search/config/meiliSearchConfig";
import {
  SearchClearLanguageFiltersButton,
  SearchHitsPerPageSelector,
  SearchLanguageRefinements,
  SearchSemanticRatioSlider,
} from "@/features/search/components/MeiliSearchControls";
import {
  useAuth,
  useCheckSearchControls,
  useSearchQueryCheck,
} from "@/state/globalStore";
import BrandedPrototypeSelector from "@/shared/ui/controls/BrandedPrototypeSelector";
import ContentItemDetailModeHeader from "@/shared/ui/display/ContentItemDetailModeHeader";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import appSettings from "@/shared/config/appSettings";
import {
  mindstringPrototypesSlang,
  normalizeVisibleMindstringPrototype,
} from "@/shared/config/backend";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_MESSAGES,
  ProfileReviewScopeControls,
} from "@/features/profile-shaping";
import SuggestedThoughtsReviewList from "./SuggestedThoughtsReviewList";
import useSuggestedThoughtsReviewController from "./useSuggestedThoughtsReviewController";
import { MAX_SUGGESTED_THOUGHTS_DISPLAY } from "./useSuggestedThoughtsReviewQuery";

type SuggestedThoughtsReviewSurfaceMode = "review" | "search_settings";
type SuggestedThoughtsReviewPaging = {
  pageCount: number;
  scopeKey: string;
};

type SuggestedThoughtsReviewScopeItem = {
  colorPalette?: "blue" | "gray" | "green" | "orange" | "purple" | "teal";
  label: string;
};

type SuggestedThoughtsReviewSurfaceProps = {
  deferSearchUntilResults?: boolean;
  enabled?: boolean;
  isExtracting?: boolean;
  hasRunExtraction?: boolean;
  originId?: string | null;
  orientationDescription?: string;
  prototype: MindstringPrototype;
  showOrientation?: boolean;
  showPrototypeSelector?: boolean;
  showSituationSelector?: boolean;
  situation?: Situation | null;
  scopeItems?: SuggestedThoughtsReviewScopeItem[];
};

function formatSituationScopeLabel(situation: Situation | null | undefined) {
  if (!situation) {
    return "All situations";
  }

  const contextLabel = [situation.position, situation.context]
    .filter(Boolean)
    .join(", ");

  return situation.abbr || contextLabel || "Chosen situation";
}

function formatSourceScopeLabel(originId: string) {
  return `Source ${originId.slice(0, 8)}`;
}

export default function SuggestedThoughtsReviewSurface({
  deferSearchUntilResults = false,
  enabled = true,
  hasRunExtraction = false,
  isExtracting = false,
  originId,
  orientationDescription = PROFILE_SHAPING_MESSAGES.reviewSuggestions,
  prototype,
  showOrientation = true,
  showPrototypeSelector = true,
  showSituationSelector = true,
  situation,
  scopeItems,
}: SuggestedThoughtsReviewSurfaceProps) {
  const { isUserAdmin } = useAuth();
  const [selectedPrototype, setSelectedPrototype] =
    useState<MindstringPrototype>(prototype);
  const [targetSituation, setTargetSituation] = useState<Situation[]>(
    situation ? [situation] : [],
  );
  const [mode, setMode] =
    useState<SuggestedThoughtsReviewSurfaceMode>("review");
  const [suggestionsPaging, setSuggestionsPaging] =
    useState<SuggestedThoughtsReviewPaging>({
      pageCount: 1,
      scopeKey: "",
    });
  const { searchQuery, setSearchQuery, resetSearchQuery } =
    useSearchQueryCheck();
  const checkSearchControls = useCheckSearchControls();
  const deferredSearchQuery = useDeferredValue(searchQuery.trim());
  const { mindstringPrototypesSlangItems } = useOptionItems();
  const situationsQuery = useSituationsQuery();
  const activePrototype = normalizeVisibleMindstringPrototype(
    showPrototypeSelector ? selectedPrototype : prototype,
    isUserAdmin,
  );
  const activeSituation = showSituationSelector
    ? (targetSituation[0] ?? null)
    : (situation ?? null);
  const selectedPrototypes = useMemo(
    () => [activePrototype],
    [activePrototype],
  );
  const { items: situationItems, situations: assignableSituations } =
    useAssignableSituations(situationsQuery.data, selectedPrototypes);
  const situationCountsByPrototype = useMemo(
    () => countSituationsByPrototype(situationsQuery.data),
    [situationsQuery.data],
  );
  const prototypeItemsWithCounts = useMemo(
    () =>
      mindstringPrototypesSlangItems.map((item) => ({
        ...item,
        count: situationCountsByPrototype[item.value],
      })),
    [mindstringPrototypesSlangItems, situationCountsByPrototype],
  );
  const hasActiveSearch = Boolean(deferredSearchQuery);
  const languageFilterKey = checkSearchControls.filters.language.join(":");
  const pagingScopeKey = [
    checkSearchControls.hitsPerPage,
    checkSearchControls.semanticRatio,
    checkSearchControls.sortBy,
    deferredSearchQuery,
    languageFilterKey,
    originId ?? "all",
    activePrototype,
    activeSituation?.id ?? "all",
  ].join("|");
  const activePageCount =
    suggestionsPaging.scopeKey === pagingScopeKey
      ? suggestionsPaging.pageCount
      : 1;
  const suggestionsLimit = checkSearchControls.hitsPerPage * activePageCount;
  const reviewController = useSuggestedThoughtsReviewController({
    enabled,
    languageFilters: checkSearchControls.filters.language,
    limit: suggestionsLimit,
    originId,
    prototype: activePrototype,
    queryText: deferredSearchQuery,
    semanticRatio: hasActiveSearch ? checkSearchControls.semanticRatio : 0,
    situationId: activeSituation?.id ?? null,
    sortBy: hasActiveSearch ? undefined : checkSearchControls.sortBy,
  });
  const prototypeLabel =
    mindstringPrototypesSlang[activePrototype]?.label ?? activePrototype;
  const situationScopeLabel = formatSituationScopeLabel(activeSituation);
  const hasLanguageFilters = checkSearchControls.filters.language.length > 0;
  const suggestedSearchConfig = MEILI_SEARCH_CONFIG.suggested_thoughts;
  const defaultSuggestedSortBy = suggestedSearchConfig.sortBy[0]?.value ?? "";
  const hasNonDefaultSearchAdjustments =
    checkSearchControls.hitsPerPage !== appSettings.search.HITS_PER_PAGE ||
    checkSearchControls.sortBy !== defaultSuggestedSortBy ||
    checkSearchControls.semanticRatio !== 0.5 ||
    hasLanguageFilters;
  const hasSuggestions = reviewController.suggestions.length > 0;
  const shouldShowSearchControls =
    !deferSearchUntilResults ||
    hasSuggestions ||
    hasActiveSearch ||
    hasNonDefaultSearchAdjustments;
  const isInitialExtractionEmptyState =
    deferSearchUntilResults &&
    !hasRunExtraction &&
    !isExtracting &&
    !reviewController.isLoadingSuggestions &&
    !hasSuggestions &&
    !hasActiveSearch &&
    !hasNonDefaultSearchAdjustments;
  const resolvedScopeItems = useMemo<SuggestedThoughtsReviewScopeItem[]>(
    () =>
      scopeItems ?? [
        ...(originId
          ? [
              {
                label: formatSourceScopeLabel(originId),
                colorPalette: "blue" as const,
              },
            ]
          : []),
        {
          label: prototypeLabel,
          colorPalette: "purple",
        },
        {
          label: situationScopeLabel,
          colorPalette: activeSituation ? "teal" : "gray",
        },
      ],
    [
      activeSituation,
      originId,
      prototypeLabel,
      scopeItems,
      situationScopeLabel,
    ],
  );
  const handleSelectedPrototypeChange = useCallback(
    (nextPrototype: MindstringPrototype) => {
      setSelectedPrototype(nextPrototype);

      const currentTargetSituation = targetSituation[0];
      if (
        currentTargetSituation &&
        !currentTargetSituation.prototypes.includes(nextPrototype)
      ) {
        setTargetSituation([]);
      }
    },
    [targetSituation],
  );
  const handleResetSearchSettings = useCallback(() => {
    checkSearchControls.resetSearchAdjustments();
    resetSearchQuery();
  }, [checkSearchControls, resetSearchQuery]);
  const handleLongPressResetSearchSettings = useCallback(() => {
    checkSearchControls.resetSearchAdjustments();
  }, [checkSearchControls]);

  if (mode === "search_settings") {
    const handleLanguageToggle = (language: string) => {
      const currentLanguages = checkSearchControls.filters.language;
      const nextLanguages = currentLanguages.includes(language)
        ? currentLanguages.filter((value) => value !== language)
        : [...currentLanguages, language];
      checkSearchControls.setFilters({ language: nextLanguages });
    };

    return (
      <VStack align="stretch" spaceY="4">
        <ContentItemDetailModeHeader
          title="Search settings"
          onReturn={() => {
            setMode("review");
          }}
          returnAriaLabel="Back to suggestion review"
        />

        <GenericDrawerInfo
          description="Tune how suggested-thought search behaves inside this review task."
          items={[
            {
              label: hasActiveSearch ? "Search active" : "No search text",
              colorPalette: hasActiveSearch ? "blue" : "gray",
            },
            {
              label: hasNonDefaultSearchAdjustments
                ? "Custom settings"
                : "Default settings",
              colorPalette: hasNonDefaultSearchAdjustments ? "purple" : "gray",
            },
          ]}
        />

        <VStack align="stretch" spaceY="3">
          <HStack justify="flex-end" gap="2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleResetSearchSettings}
              disabled={!hasActiveSearch && !hasNonDefaultSearchAdjustments}
              aria-label="Reset suggestion search settings"
            >
              <GenericIcon
                icon="reset"
                size={iconSizePresets.actionButton}
                highlighted={hasActiveSearch || hasNonDefaultSearchAdjustments}
              />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Reset
              </Text>
            </Button>
          </HStack>

          <SearchSemanticRatioSlider
            stateObject={[checkSearchControls.semanticRatio]}
            stateObjectSetter={(values: number[]) => {
              if (values.length) {
                checkSearchControls.setSemanticRatio(values[0]);
              }
            }}
            title="Semantic-to-keyword search ratio"
            isDecimal={true}
          />

          <SearchHitsPerPageSelector
            value={checkSearchControls.hitsPerPage}
            onChange={checkSearchControls.setHitsPerPage}
            items={DEFAULT_HITS_PER_PAGE.map((item) => ({
              value: parseInt(item.value, 10),
              label: item.label,
            }))}
          />

          {!hasActiveSearch ? (
            <GenericSelector
              previewItems={suggestedSearchConfig.sortBy}
              stateObject={[checkSearchControls.sortBy]}
              stateObjectSetter={(values: string[]) => {
                if (values.length) {
                  checkSearchControls.setSortBy(values[0]);
                }
              }}
              labelText="Sort by"
              mode="select"
              placeholder="Sort by..."
              disableClear={true}
            />
          ) : null}

          <SearchLanguageRefinements
            selectedLanguages={checkSearchControls.filters.language}
            onLanguageToggle={handleLanguageToggle}
            title="Filter by language"
          />

          <SearchClearLanguageFiltersButton
            onClear={() => {
              checkSearchControls.setFilters({ language: [] });
            }}
            hasFilters={hasLanguageFilters}
          />
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spaceY="4">
      {showOrientation ? (
        <GenericDrawerInfo
          description={orientationDescription}
          items={[
            {
              label: PROFILE_SHAPING_LABELS.reviewPhase,
              colorPalette: "blue",
            },
            ...resolvedScopeItems,
            {
              label: `${reviewController.suggestions.length} suggestions`,
              colorPalette:
                reviewController.suggestions.length > 0 ? "blue" : "gray",
            },
            ...(hasActiveSearch
              ? [
                  {
                    label: "Search active",
                    colorPalette: "blue" as const,
                  },
                ]
              : []),
          ]}
        />
      ) : null}

      {shouldShowSearchControls ? (
        <ProfileReviewScopeControls
          controls={
            <>
              {showPrototypeSelector ? (
                <BrandedPrototypeSelector
                  activePrototype={activePrototype}
                  onChange={handleSelectedPrototypeChange}
                  options={prototypeItemsWithCounts}
                  ariaLabel="Choose perspective"
                  showCounts={true}
                  showDescriptions={true}
                  variant="compact"
                />
              ) : null}
              {showSituationSelector ? (
                <GenericSelector<Situation>
                  key={activePrototype}
                  previewItems={situationItems}
                  stateObject={targetSituation}
                  stateObjectSetter={setTargetSituation}
                  mode="select"
                  sourceItems={assignableSituations}
                  placeholder="All situations"
                  width="full"
                  isLoading={situationsQuery.isLoading}
                />
              ) : null}
            </>
          }
          onOpenSearchSettings={() => {
            setMode("search_settings");
          }}
          onLongPressResetSearchSettings={handleLongPressResetSearchSettings}
          onResetSearchQuery={resetSearchQuery}
          onSearchQueryChange={setSearchQuery}
          searchAriaLabel="Search suggestions"
          searchPlaceholder="Search suggestions"
          searchQuery={searchQuery}
          searchSettingsHighlighted={hasNonDefaultSearchAdjustments}
          searchSettingsLabel="Search settings"
          searchSettingsResetEnabled={hasNonDefaultSearchAdjustments}
          voiceSearchSource={originId ? "backlog" : "studio"}
        />
      ) : null}

      <SuggestedThoughtsReviewList
        canLoadMoreSuggestions={reviewController.canLoadMoreSuggestions}
        canSelectAllSuggestedThoughts={
          reviewController.canSelectAllSuggestedThoughts
        }
        emptyState={
          isInitialExtractionEmptyState
            ? {
                icon: "origin_source",
                title: "Shape this source first",
                message:
                  "Use Shape from source above. Suggestions will appear here as soon as the source has been shaped.",
              }
            : undefined
        }
        isExtracting={isExtracting}
        isLoadingSuggestions={reviewController.isLoadingSuggestions}
        maxSuggestions={MAX_SUGGESTED_THOUGHTS_DISPLAY}
        onConfirmSelectedSuggestedThoughts={
          reviewController.handleConfirmSelectedSuggestedThoughts
        }
        onCopySelectedSuggestedThoughts={
          reviewController.handleCopySelectedSuggestedThoughts
        }
        onDeleteSelectedSuggestedThoughts={
          reviewController.handleDeleteSelectedSuggestedThoughts
        }
        onLoadMoreSuggestions={() => {
          setSuggestionsPaging((currentPaging) => ({
            scopeKey: pagingScopeKey,
            pageCount:
              currentPaging.scopeKey === pagingScopeKey
                ? currentPaging.pageCount + 1
                : 2,
          }));
        }}
        onSelectAllSuggestedThoughts={
          reviewController.handleSelectAllSuggestedThoughts
        }
        onSuggestedThoughtReviewSync={
          reviewController.handleSuggestedThoughtReviewSync
        }
        onUnselectSelectedSuggestedThoughts={
          reviewController.handleUnselectSelectedSuggestedThoughts
        }
        selectedSuggestedThoughtCount={
          reviewController.selectedSuggestedThoughtCount
        }
        searchQuery={deferredSearchQuery}
        scopeItems={resolvedScopeItems}
        suggestions={reviewController.suggestions}
      />
    </VStack>
  );
}
